import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLinkIcon } from 'lucide-react';
import { PageHeader } from './ui/PageHeader';
import { Panel } from './ui/Panel';
import { ContentSections, type ContentBlock } from './ui/ContentSections';
import { pageSections, type EditablePage } from '../data/pageSections';
import { PageTextSearch, type SearchHit } from './PageTextSearch';
import type { Json } from './ui/ContentEditor';
import { btnGhost, inputCls } from './ui/styles';
import { useAdmin } from './AdminContext';
import { tokenHelp } from './adminLabels';
import { shopHref } from '../utils/hosts';
import { SeoPanel } from './ui/SeoPanel';
import { isSeoPageKey, pageSeoAuto } from '../utils/seoAuto';
import type { PageContent } from '../core/types/pages';
const sections: {
  key: Exclude<keyof PageContent, 'seo'>;
  label: string;
  path: string;
  description: string;
}[] = [{
  key: 'home',
  label: 'Home',
  path: '/',
  description: 'De homepage is één verhaal: Kijken, Kiezen, Opbouwen, en tot slot Kom langs. De volgorde ligt vast; jij bepaalt de woorden en beelden. De producten en protocollen kies je met “uitgelicht” in Aanbod.'
}, {
  key: 'shop',
  label: 'Webshop',
  path: '/webshop',
  description: 'De kop met het beeld ernaast, en het blok over protocollen onder de collectie. In de kop ook {count} (aantal producten) en {brands} (de merken).'
}, {
  key: 'routines',
  label: 'Protocollen',
  path: '/routines',
  description: 'De blokken in de volgorde van de pagina. De hoofdknop “Begin met je protocol” ligt vast; de protocollen zelf staan in Aanbod. De klachtkiezer en de tijdlijn zijn dezelfde als op Home (hier zonder beweging); de klachten en de momenten pas je daar aan.'
}, {
  key: 'treatments',
  label: 'Salon',
  path: '/behandelingen',
  description: 'De blokken in de volgorde van de pagina. Elke boekknop heet “Plan een afspraak”; de behandeling kies je in de lade.'
}, {
  key: 'journal',
  label: 'Journal',
  path: '/journal',
  description: 'Koppen, de podcast (met een link per aflevering) en de regel naar Instagram. De artikelen zelf staan in Aanbod › Journal; de Instagram-link in Instellingen › Salon & contact.'
}, {
  key: 'about',
  label: 'Over Sarah',
  path: '/over-ons',
  description: 'Je verhaal in vijf blokken: wie je bent, hoe het begon (met je opleidingen en de pers), waar je voor staat, je eigen plank en kom kennismaken. De hoofdknop “Bekijk de collectie” ligt vast. Vaste cliënten staan bij de reviews (Instellingen › Cadeaus & codes).'
}, {
  key: 'contact',
  label: 'Contact',
  path: '/contact',
  description: 'Kop, de soorten vragen met hun knop, het grote beeld en het blok over langskomen. Adres en route staan in Instellingen › Salon & contact, de dagen in › Salondagen.'
}, {
  key: 'portal',
  label: 'Mijn omgeving',
  path: '/account',
  description: 'Tips van de week, seizoensmeldingen en de redenen bij stoppen met het abonnement. Extra slimme woorden per tip: {product}, {week}, {weeks}, {treatment}, {courseSpacing}.'
}, {
  key: 'chrome',
  label: 'Kop en voet',
  path: '/',
  description: 'Wat onder elke pagina staat. Adres, tijden en links komen uit Instellingen › Salon & contact. De knoppen in de header en het menu (“Plan een afspraak”, “Huidtest”) liggen vast, zodat ze overal hetzelfde heten.'
}, {
  key: 'testimonials',
  label: 'Klantcitaten',
  path: '/',
  description: 'De citaten op de homepage. De eerste staat groot.'
}, {
  key: 'subscription',
  label: 'Bewust Abonnement',
  path: '/webshop',
  description: 'De belofte, drie stappen en de voordelen. Op Webshop en Protocollen; de knop opent de samensteller.'
}];

/** De blokken van een pagina in de volgorde van de site, met rol, toon en een link naar dat blok. */
const blocksFor = (key: EditablePage, path: string): ContentBlock[] | undefined => pageSections[key]?.map(s => ({
  key: s.key,
  title: s.title,
  role: s.role,
  tone: s.tone,
  labels: s.labels,
  href: shopHref(s.anchor ? `${path}#${s.anchor}` : path)
}));

/** Alle vaste teksten per pagina, met uitleg over de slimme woorden. */
export function PageTexts() {
  const {
    pages,
    setPages,
    changed
  } = useAdmin();
  const [params, setParams] = useSearchParams();
  const [tokensOpen, setTokensOpen] = useState(false);
  const active = sections.find(s => s.key === params.get('pagina')) ?? sections[0];
  const [focus, setFocus] = useState<(SearchHit & {
    n: number;
  }) | null>(null);
  const searchable = useMemo(() => sections.map(s => ({
    key: s.key,
    label: s.label,
    value: pages[s.key] as unknown as Json
  })), [pages]);
  const pick = (hit: SearchHit) => {
    setParams({
      pagina: hit.page
    }, {
      replace: true
    });
    // Teller erbij: twee keer hetzelfde blok kiezen opent en scrollt opnieuw.
    setFocus(f => ({
      ...hit,
      n: (f?.n ?? 0) + 1
    }));
  };
  const copyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      toast(`${token} gekopieerd`, {
        description: 'Plak het in een tekstveld.'
      });
    } catch {
      toast(token);
    }
  };
  return <div data-id="element-3753" data-mp="ukzl8qseg8ju">
      <PageHeader title="Paginateksten" description="Alle vaste teksten op de website. De opbouw van de pagina blijft gelijk; jij bepaalt de woorden." data-id="element-3754" data-mp="ukr95143p79p" />

      <div className="mb-6 max-w-xl" data-id="element-3755" data-mp="llcfjssn0gkz">
        <PageTextSearch pages={searchable} onPick={pick} data-id="element-3756" data-mp="loi5fa93bmhr" />
      </div>

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-3757" data-mp="6vy84q8mg988">
        <nav aria-label="Pagina’s" className="lg:col-span-3" data-id="element-3758" data-mp="bnzha3ahtju1">
          {/* Telefoon: de eigen keuzelijst van het toestel. Negen pagina's passen niet naast elkaar,
              en een scrollrij verstopt de helft; de native lijst toont alles en is groot genoeg om te tikken. */}
          <label className="block lg:hidden" data-id="element-3759" data-mp="2tjnq1pb11eu">
            <span className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-3760" data-mp="3k52lqehyqn40">Pagina</span>
            <select value={active.key} onChange={e => setParams({
            pagina: e.target.value
          }, {
            replace: true
          })} className={inputCls} data-id="element-3761" data-mp="3p3dvkp89cxm8">
              {sections.map(s => <option key={s.key} value={s.key} data-id="element-3762" data-mp="3167wfz6mwpp0">
                  {s.label}
                </option>)}
            </select>
          </label>
          <ul className="hidden flex-col gap-1 lg:flex" data-id="element-3763" data-mp="2ra4tir3ykn10">
            {sections.map(s => <li key={s.key} data-id="element-3764" data-mp="3ox3liojph6sk">
                <button type="button" onClick={() => setParams({
              pagina: s.key
            }, {
              replace: true
            })} aria-current={active.key === s.key ? 'page' : undefined} className={`w-full rounded-md px-3 py-2 text-left text-[14px] transition-colors duration-150 ease-soft ${active.key === s.key ? 'bg-white text-ink shadow-sm ring-1 ring-line' : 'text-muted hover:bg-white/60 hover:text-ink'}`} data-id="element-3765" data-mp="3ow26ya3he263">
                  {s.label}
                </button>
              </li>)}
          </ul>

          <div className="mt-6 hidden rounded-lg border border-line bg-white p-4 lg:block" data-id="element-3766" data-mp="e4hpqtvv4z8n">
            <p className="text-[13px] font-medium text-ink" data-id="element-3767" data-mp="j2j6h85tgmgy">Slimme woorden</p>
            <p className="mt-1 text-[12px] leading-relaxed text-muted" data-id="element-3768" data-mp="o01ij7f4rht6">Deze vult de site zelf in. Verandert je telefoonnummer, dan klopt elke tekst meteen.</p>
            <ul className="mt-3 space-y-1.5" data-id="element-3769" data-mp="xqw5ke07alfo">
              {tokenHelp.map(t => <li key={t.token} data-id="element-3770" data-mp="xsywd096gilg">
                  <button type="button" onClick={() => void copyToken(t.token)} aria-label={`${t.token} kopiëren: ${t.meaning}`} title="Klik om te kopiëren" className="group -mx-1.5 block w-full rounded-md px-1.5 py-1 text-left text-[12px] transition-colors duration-150 ease-soft hover:bg-shell" data-id="element-3771" data-mp="11ry56vy14zi6">
                    <code className="rounded bg-sand px-1.5 py-0.5 text-ink group-hover:bg-blush" data-id="element-3772" data-mp="3dj360l44azsz">{t.token}</code>{' '}
                    <span className="text-muted" data-id="element-3773" data-mp="1j8lmg0ee8enx">{t.meaning}</span>
                  </button>
                </li>)}
            </ul>
          </div>
        </nav>

        <div className="lg:col-span-9" data-id="element-3774" data-mp="1t099lp3x39jl">
          <Panel title={active.label} description={active.description} actions={<a href={shopHref(active.path)} target="_blank" rel="noopener noreferrer" className={`${btnGhost} shrink-0`} data-id="element-3776" data-mp="10bc12dpicr41">
                Bekijk pagina
                <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-3777" data-mp="vdisje0qfl0l" />
                <span className="sr-only" data-id="element-3778" data-mp="1aa9kx3bznwgp"> (opent in een nieuw tabblad)</span>
              </a>} data-id="element-3775" data-mp="1t22eayuyzjoy">
            <button type="button" onClick={() => setTokensOpen(o => !o)} aria-expanded={tokensOpen} aria-controls="token-help" className="mb-5 text-[12px] text-clay underline underline-offset-2 lg:hidden" data-id="element-3779" data-mp="1593rf3ai8564">
              {tokensOpen ? 'Verberg slimme woorden' : 'Wat betekent {firstName}?'}
            </button>
            {tokensOpen && <ul id="token-help" className="mb-5 grid gap-1 rounded-md bg-shell p-3 text-[12px] lg:hidden" data-id="element-3780" data-mp="2f8en54bd013z">
                {tokenHelp.map(t => <li key={t.token} data-id="element-3781" data-mp="2jart9tu0q11a">
                    <code className="text-ink" data-id="element-3782" data-mp="2jbtcill6ttu3">{t.token}</code> <span className="text-muted" data-id="element-3783" data-mp="370m280b7v5g9">{t.meaning}</span>
                  </li>)}
              </ul>}
            <ContentSections key={active.key} value={pages[active.key] as unknown as Json} onChange={next => setPages(p => ({
            ...p,
            [active.key]: next
          }))} focusKey={focus?.page === active.key ? focus.block : null} focusTick={focus?.n} blocks={blocksFor(active.key, active.path)} data-id="element-3784" data-mp="3c0ry5soire45" />
          </Panel>
          {isSeoPageKey(active.key) && <div className="mt-6" data-id="element-3785" data-mp="2ttk7cld3r7zl">
              <SeoPanel key={active.key} seo={pages.seo?.[active.key]} onChange={seo => {
            const key = active.key;
            if (!isSeoPageKey(key)) return;
            setPages(p => ({
              ...p,
              seo: {
                ...p.seo,
                [key]: seo
              }
            }));
          }} auto={pageSeoAuto(active.key)} path={active.path} imageFallback="de algemene deelfoto van de site" data-id="element-3786" data-mp="2tumvwmmycycc" />
            </div>}
          {changed.pages && <p className="mt-3 text-[12px] text-muted" data-id="element-3787" data-mp="2fw5et7rjyk3e">Vergeet niet rechtsboven op Opslaan te klikken.</p>}
        </div>
      </div>
    </div>;
}